import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AuthUser } from '../types';
import { fetchCurrentUser, logout as logoutRequest, startGoogleLogin } from '../services/authService';

type AuthContextValue = {
  user: AuthUser | null;
  loading: boolean;
  loginWithGoogle: () => void;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    fetchCurrentUser()
      .then(currentUser => {
        if (!cancelled) {
          setUser(currentUser);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUser(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (loading || user) {
      return;
    }

    let inFlight = false;

    const recheckAuth = () => {
      if (inFlight) {
        return;
      }

      inFlight = true;
      fetchCurrentUser()
        .then(setUser)
        .catch(() => {
          // Ignore and keep login UI when user is actually unauthenticated.
        })
        .finally(() => {
          inFlight = false;
        });
    };

    window.addEventListener('focus', recheckAuth);
    window.addEventListener('online', recheckAuth);

    return () => {
      window.removeEventListener('focus', recheckAuth);
      window.removeEventListener('online', recheckAuth);
    };
  }, [loading, user]);

  const loginWithGoogle = () => {
    startGoogleLogin();
  };

  const logout = async () => {
    await logoutRequest();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
